import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ALLOWED_FILES_IN_CHAT, AvailableLanguages, FILE_SIZE_LIMIT, patterns } from '@constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { AllDeviceInfo, CreateMessageInMyChat, InAppNotificationType, User, UserAsset } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';

import { ChatFacade } from '@state/chat';

import { environment } from '@environment';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { FileHandlerService } from '@core/services/file-handler.service';
import { RemoveTagsService } from '@shared/remove-tags/remove-tags.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { InAppNotificationsFacade } from '@state/notifications';
import { UserAuthFacade } from '@state/user-auth';
import { isString } from 'lodash-es';
import { ReplaySubject, lastValueFrom } from 'rxjs';
import { delay, filter, map, take, takeUntil } from 'rxjs/operators';
import { ChatAttachmentsService } from '../chat-attachments.service';
import { ChatService } from '../chat.service';

@Component({
  selector: 'app-chat-desktop',
  templateUrl: './chat-desktop.component.html',
  styleUrls: ['./chat-desktop.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translate3d(16px, 16px, 0) scale(0.94)' }),
        animate('220ms ease-out', style({ opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }),
        animate('180ms ease-in', style({ opacity: 0, transform: 'translate3d(16px, 16px, 0) scale(0.94)' })),
      ]),
    ]),
  ],
  standalone: false,
})
export class ChatDesktopComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly lsService = inject(LocalStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly inAppNotification = inject(InAppNotificationsFacade);
  private readonly chatService = inject(ChatService);
  private readonly chatFacade = inject(ChatFacade);
  private readonly removeTagsService = inject(RemoveTagsService);
  private readonly fileHandlerService = inject(FileHandlerService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly chatAttachmentsService = inject(ChatAttachmentsService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('chatModuleWrapper') chatModuleWrapper: ElementRef<HTMLElement>;
  @ViewChild('chatMessageInput') chatMessageInput: ElementRef;
  @ViewChild('chatMessagesContainer') chatMessagesContainer: ElementRef;
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef<HTMLElement>;

  public seenGeneralChatHint: boolean;
  public chatWindowOpened = false;
  public myself: User;

  public showLoader = true;
  public hasAnyNewMessages: boolean;
  public chatLauncherLoading = false;

  public currentLang: AvailableLanguages;

  public loading$ = this.chatFacade.loading$;
  public chatMessages$ = this.chatFacade.chatMessages$;

  public deviceInfo: AllDeviceInfo;

  public attachmentsToAdd$ = this.chatFacade.attachmentsToAdd$.pipe(
    filter(items => !!items),
    map(items => this.chatAttachmentsService.mapAttachments(items))
  );

  public newMessageContent = '';
  public messageAttachments: string[] = [];
  public uploadingAttachments = false;
  public hasAttachmentsToAdd = false;
  public API_URL = environment.API_URL;
  private readonly chatLauncherMinLoadingTimeMs = 450;
  private chatDataLoading = false;
  private chatLoaded = false;
  private chatLauncherLoadingStartedAt = 0;
  private chatLauncherLoadingHideTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];
  private boundClickListener = this.clickListener.bind(this);

  // ngOnInit executed as soon as the chat icon appears!
  async ngOnInit(): Promise<void> {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    this.seenGeneralChatHint = this.lsService.getGeneralChatHintSeen();
    this.currentLang = this.translateService.currentLang as AvailableLanguages;

    this.userAuthFacade.myself$
      .pipe(
        filter(myself => !!myself?.id),
        takeUntil(this.destroy$)
      )
      .subscribe(async myself => {
        this.myself = myself;
      });

    this.userAuthFacade.myself$
      .pipe(
        filter(myself => !!myself?.id),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const notificationType = InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT;
        this.inAppNotification
          .getUnreadCountOfType$(notificationType, null)
          .pipe(delay(100), take(1), takeUntil(this.destroy$))
          .subscribe(unreadCount => {
            this.hasAnyNewMessages = unreadCount !== null && unreadCount > 0;
          });

        this.chatFacade.getMyChat$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
      });

    this.chatFacade.myChat$.pipe(takeUntil(this.destroy$)).subscribe(chat => {
      this.chatLoaded = !!chat;
      this.updateChatLauncherLoading();
    });

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.chatDataLoading = loading;
      this.updateChatLauncherLoading();
    });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });
    document.addEventListener('click', this.boundClickListener);
  }

  async ngAfterViewInit(): Promise<void> {
    this.showLoader = true;

    this.chatFacade.addChatMessageSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.newMessageContent = '';
      this.setSafeTimeout(() => this.scrollToBottom(), 100);
    });

    this.attachmentsToAdd$.pipe(takeUntil(this.destroy$)).subscribe(attachments => {
      this.hasAttachmentsToAdd = !!attachments?.length;
      if (!attachments?.length) {
        this.messageAttachments = [];
        this.uploadingAttachments = false;
        return;
      }

      this.uploadingAttachments = attachments.some(attachment => attachment.loading);
      this.messageAttachments = this.chatAttachmentsService.getUploadedAttachmentIds(attachments);
    });

    this.chatFacade.getMyChatSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setSafeTimeout(() => this.scrollToBottom());
    });

    this.inAppNotification.newNotificationReceived$.pipe(takeUntil(this.destroy$)).subscribe(({ notification }) => {
      // interested only in this type
      if (InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT !== notification.type) {
        return;
      }

      this.hasAnyNewMessages = true;
      this.chatFacade.getMyChat();
    });

    this.showLoader = false;

    this.chatService.showChat$
      .asObservable()
      .pipe(delay(50), takeUntil(this.destroy$))
      .subscribe(({ show }) => {
        if (!show) {
          this.chatWindowOpened = false;
          return;
        }

        this.toggleChatWindow();
      });
  }

  ngOnDestroy(): void {
    this.clearPendingTimeouts();
    this.clearChatLauncherLoadingHideTimeout();
    document.removeEventListener('click', this.boundClickListener);
    this.chatService.showChat$.next({ show: false, relatedForeignerId: '' });
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public async uploadFiles($event: any): Promise<void> {
    const files = $event.target.files as FileList;
    const validatedFiles = [];
    const invalidFiles = [];
    const MAX_NUMBER_OF_FILES = 10;

    const existingItems = await lastValueFrom(this.attachmentsToAdd$.pipe(take(1), takeUntil(this.destroy$)));
    const selectedFileNames = new Set<string>();

    if (files.length > MAX_NUMBER_OF_FILES) {
      this.stackedMessages.showWarning({
        message: 'COMMON.VALIDATOR.YOU_CAN_UPLOAD_MAX_10_AT_A_TIME',
        duration: 4500,
      });
    }

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      // If user managed to upload some wierd file
      // Let him - we don't care that app is not working now - his fault.
      if (!file?.size || !file?.type) {
        this.stackedMessages.showError({
          message: 'CHAT.CANNOT_READ_FILE_CORRECTLY',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      if (file.size > FILE_SIZE_LIMIT) {
        this.stackedMessages.showError({
          message: 'CHAT.FILE_NOT_ADDED_BECAUSE_TOO_BIG',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      if (!ALLOWED_FILES_IN_CHAT.includes(file.type.toLowerCase())) {
        this.stackedMessages.showError({
          message: 'CHAT.FILE_NOT_ADDED_BECAUSE_INVALID',
          translateParams: { fileName: file.name },
          duration: 6500,
        });
        invalidFiles.push(file);
        continue;
      }

      const fileWithSuchNameAlreadyAdded = existingItems?.find(
        ({ fileOriginalName }) => fileOriginalName === file.name
      );
      if (fileWithSuchNameAlreadyAdded || selectedFileNames.has(file.name)) {
        continue;
      }

      selectedFileNames.add(file.name);
      validatedFiles.push(file);

      // max 10 at a time
      if (validatedFiles.length === 10) {
        break;
      }
    }

    if (invalidFiles.length) {
      this.stackedMessages.showError({
        message: 'CHAT.SOME_OF_THE_FILES_NOT_ADDED',
        duration: 8500,
      });
    }

    (this.fileUploadInput.nativeElement as any).value = '';
    if (!validatedFiles.length) {
      return;
    }

    validatedFiles.forEach(validatedFile => {
      this.chatFacade.uploadMessageAttachments({
        files: [{ file: validatedFile, fileOriginalName: validatedFile.name }],
      });
    });
  }

  public downloadMessageAttachment(messageId: string, attachmentId: string, fileOriginalName?: string | null): void {
    const fileExtension = this.getFileExtension(fileOriginalName);
    const previewTargetKey = this.fileHandlerService.canOpenInBrowser(fileExtension, null)
      ? this.fileHandlerService.preparePreviewTarget(attachmentId)
      : undefined;

    this.chatFacade.downloadAttachmentFromChat(messageId, attachmentId, previewTargetKey);
  }

  private getFileExtension(fileOriginalName?: string | null): string {
    if (!fileOriginalName) {
      return '';
    }

    const lastDotIndex = fileOriginalName.lastIndexOf('.');

    return lastDotIndex >= 0 ? fileOriginalName.slice(lastDotIndex) : '';
  }

  public removeAsset(userAsset: UserAsset): void {
    this.chatFacade.removeAttachmentFromUploading({
      fileOriginalName: userAsset.fileOriginalName,
      userAssetId: userAsset.id,
    });
  }

  private clickListener($event: MouseEvent): void {
    // if window closed - do nothing
    if (!this.chatWindowOpened) {
      return;
    }

    const eventPath = typeof $event.composedPath === 'function' ? $event.composedPath() : [];

    // if click has been  inside window - do nothing
    if (
      eventPath.includes(this.chatModuleWrapper.nativeElement) ||
      this.chatModuleWrapper.nativeElement.contains($event.target as Node)
    ) {
      return;
    }

    const htmlTarget = $event.target as HTMLElement;

    if (htmlTarget?.closest('.bridge-stacked-messages-host, .bridge-stacked-message')) {
      return;
    }

    if (
      htmlTarget?.classList?.contains('open-chat-button') ||
      htmlTarget?.classList?.contains('chat-message-action') ||
      htmlTarget?.classList?.contains('negative') ||
      htmlTarget?.classList?.contains('cancel-edit-chatMessage-icon')
    ) {
      return;
    }

    // mat-dialog is in cdk-overlay which is completely separate element
    // If user revealed mat-dialog (confirmation window) clicked outside of it
    // condition below will stop closing of chat-window before user closes mat-dialog
    const cdkOverlay = document.getElementsByClassName('cdk-overlay-backdrop');
    if (cdkOverlay && cdkOverlay[0]?.contains($event.target as any)) {
      return;
    }

    const modal = document.getElementsByClassName('confirmation-modal');
    if (modal && modal[0]?.contains($event.target as any)) {
      return;
    }

    this.toggleChatWindow();
  }

  public submitMessage($event: Event): void {
    let content = this.newMessageContent;
    const isKeyboardSubmit = $event instanceof KeyboardEvent;

    if (isKeyboardSubmit && this.deviceInfo?.deviceTypeDetected !== 'DESKTOP') {
      return;
    }

    if (($event instanceof KeyboardEvent || $event instanceof MouseEvent) && $event.shiftKey) {
      return;
    }

    $event.preventDefault();

    if (this.uploadingAttachments) {
      return;
    }

    if (content?.length) {
      content = content.trim();
      content = this.removeTagsService.removeTags(content, ['h1', 'h2', 'h3', 'br', 'b', 'i', 'li', 'ul', 'ol']);
      if (content.length === 0 && this.messageAttachments.length === 0) {
        this.stackedMessages.showError({
          message: 'CHAT.WRONG_INPUT',
          duration: 3000,
        });
      }
    }
    if (this.messageAttachments.length === 0 && (!content || !this.messageContentValid(this.newMessageContent))) {
      return;
    }

    const opts: CreateMessageInMyChat = {
      content,
      attachments: [...this.messageAttachments],
    };

    this.chatFacade.addChatMessage({ ...opts });
    this.analyticsService.trackEvent('user_event', 'user_sent_chat_message');
  }

  public scrollToBottom(behavior?: ScrollBehavior): void {
    const htmlElem = this.chatMessagesContainer?.nativeElement as HTMLElement;
    if (!htmlElem) {
      return;
    }

    const scrollHeight = htmlElem.scrollHeight;
    htmlElem.scrollTo({ top: scrollHeight, behavior: behavior || 'smooth' });
    if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
      this.chatMessageInput?.nativeElement?.focus();
    }

    this.inAppNotification.markAllOfTypeAsRead({
      inAppNotificationType: InAppNotificationType.EMPLOYEE_SENT_MESSAGE_IN_CHAT,
      relatedForeignerId: this.myself.id,
    });

    this.hasAnyNewMessages = false;
  }

  public messageContentValid(content: string): boolean {
    const pattern = patterns.CommentTextValidator;

    if (!isString(content)) {
      return false;
    }
    return !!(content as string).match(pattern);
  }

  public closeGeneralChatHint(): void {
    this.seenGeneralChatHint = true;
    this.lsService.setGeneralChatHintSeen(true);
  }

  public toggleChatWindow(): void {
    const div = document.getElementsByClassName('open-chat-icon-wrapper')[0];
    const openingChat = !this.chatWindowOpened;

    if (!openingChat && this.uploadingAttachments) {
      return;
    }

    if (openingChat && (!this.chatLoaded || this.chatDataLoading)) {
      this.setChatLauncherLoading(true);
      if (!this.chatLoaded && !this.chatDataLoading) {
        this.chatFacade.getMyChat();
      }
    }

    if (openingChat && !this.seenGeneralChatHint) {
      this.closeGeneralChatHint();
    }

    this.chatWindowOpened = !this.chatWindowOpened;
    div?.classList?.toggle('chat-opened');

    if (this.chatWindowOpened) {
      this.setSafeTimeout(() => {
        this.scrollToBottom('auto');
        this.chatMessageInput?.nativeElement?.focus();
        this.analyticsService.trackEvent('user_event', 'user_opened_chat');
        this.updateChatLauncherLoading();
      }, 100);
    } else {
      this.updateChatLauncherLoading();
    }
  }

  private updateChatLauncherLoading(): void {
    this.setChatLauncherLoading(this.chatDataLoading);
  }

  private setChatLauncherLoading(loading: boolean): void {
    if (loading) {
      this.clearChatLauncherLoadingHideTimeout();

      if (!this.chatLauncherLoading) {
        this.chatLauncherLoadingStartedAt = performance.now();
      }

      this.chatLauncherLoading = true;
      return;
    }

    if (!this.chatLauncherLoading) {
      return;
    }

    const elapsedMs = performance.now() - this.chatLauncherLoadingStartedAt;
    const remainingMs = Math.max(this.chatLauncherMinLoadingTimeMs - elapsedMs, 0);

    if (remainingMs === 0) {
      this.chatLauncherLoading = false;
      return;
    }

    this.clearChatLauncherLoadingHideTimeout();
    this.chatLauncherLoadingHideTimeout = setTimeout(() => {
      this.chatLauncherLoading = false;
      this.chatLauncherLoadingHideTimeout = null;
    }, remainingMs);
  }

  private clearChatLauncherLoadingHideTimeout(): void {
    if (!this.chatLauncherLoadingHideTimeout) {
      return;
    }

    clearTimeout(this.chatLauncherLoadingHideTimeout);
    this.chatLauncherLoadingHideTimeout = null;
  }

  private setSafeTimeout(callback: () => void, timeout = 0): void {
    const timeoutId = setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter(id => id !== timeoutId);
      callback();
    }, timeout);

    this.pendingTimeouts.push(timeoutId);
  }

  private clearPendingTimeouts(): void {
    this.pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingTimeouts = [];
  }
}
